import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db, storage } from "../config/firebaseConfig";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { Trash2, Upload, Loader, Edit2, ArrowLeft } from "lucide-react";

const isVideoUrl = (value) =>
  /\.(mp4|webm|mov|m4v)(\?|$)/i.test(String(value || ""));

export default function ManageGallerySection({
  sectionKey,
  sectionTitle,
  sectionDescription,
}) {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saveState, setSaveState] = useState({
    saving: false,
    success: "",
    error: "",
  });

  const [editingImage, setEditingImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFile, setEditFile] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const isAdminOrPrincipal =
          userSnap.exists() &&
          (userSnap.data()?.roles?.includes("admin") ||
            userSnap.data()?.roles?.includes("principal"));
        setIsAdmin(isAdminOrPrincipal);

        if (!isAdminOrPrincipal) {
          navigate("/");
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        navigate("/");
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const galleryRef = doc(db, "siteSettings", "galleryImages");

    const unsubscribe = onSnapshot(
      galleryRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setImages(data[sectionKey] || []);
        } else {
          setImages([]);
        }
        setDataLoading(false);
      },
      () => {
        setDataLoading(false);
      },
    );

    return () => unsubscribe();
  }, [sectionKey]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      setSaveState({
        saving: false,
        success: "",
        error: "Please select a valid image or video file.",
      });
      return;
    }

    setUploading(true);
    setSaveState({ saving: true, success: "", error: "" });

    try {
      const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `gallery-images/${sectionKey}/${Date.now()}-${safeFileName}`;
      const mediaRef = storageRef(storage, filePath);

      await uploadBytes(mediaRef, file);
      const fileUrl = await getDownloadURL(mediaRef);

      await setDoc(
        doc(db, "siteSettings", "galleryImages"),
        {
          [sectionKey]: arrayUnion({
            url: fileUrl,
            uploadedAt: new Date().toISOString(),
            uploadedBy: user?.uid || null,
            fileName: file.name,
            type: isVideo ? "video" : "image",
            isDefault: false,
          }),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      setSaveState({
        saving: false,
        success: "Image uploaded successfully!",
        error: "",
      });
    } catch (error) {
      console.error("Upload error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Upload failed. Please try again.",
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteImage = async (imageUrl, imageData) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return;
    }

    setSaveState({ saving: true, success: "", error: "" });

    try {
      if (!imageData.isDefault && imageUrl.includes("storage.googleapis.com")) {
        try {
          const fileRef = storageRef(storage, imageUrl);
          await deleteObject(fileRef);
        } catch (storageError) {
          console.warn("Failed to delete from storage:", storageError);
        }
      }

      await updateDoc(doc(db, "siteSettings", "galleryImages"), {
        [sectionKey]: arrayRemove(imageData),
        updatedAt: serverTimestamp(),
      });

      setSaveState({
        saving: false,
        success: "Image deleted successfully!",
        error: "",
      });
    } catch (error) {
      console.error("Delete error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Failed to delete image. Please try again.",
      });
    }
  };

  const handleEditImage = (image) => {
    setEditingImage(image);
    setShowEditModal(true);
    setEditFile(null);
  };

  const handleSaveEdit = async () => {
    if (!editingImage) return;

    setSaveState({ saving: true, success: "", error: "" });

    try {
      const updatedImage = { ...editingImage };

      if (editFile) {
        const safeFileName = editFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const filePath = `gallery-images/${sectionKey}/${Date.now()}-${safeFileName}`;
        const mediaRef = storageRef(storage, filePath);

        await uploadBytes(mediaRef, editFile);
        const newFileUrl = await getDownloadURL(mediaRef);

        if (
          !editingImage.isDefault &&
          editingImage.url.includes("storage.googleapis.com")
        ) {
          try {
            const oldFileRef = storageRef(storage, editingImage.url);
            await deleteObject(oldFileRef);
          } catch (err) {
            console.warn("Could not delete old file:", err);
          }
        }

        updatedImage.url = newFileUrl;
        updatedImage.fileName = editFile.name;
      }

      const updatedImages = images.map((img) =>
        img.url === editingImage.url ? updatedImage : img,
      );

      await setDoc(
        doc(db, "siteSettings", "galleryImages"),
        {
          [sectionKey]: updatedImages,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      setShowEditModal(false);
      setEditingImage(null);
      setEditFile(null);

      setSaveState({
        saving: false,
        success: "Image updated successfully!",
        error: "",
      });
    } catch (error) {
      console.error("Edit error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Failed to update image. Please try again.",
      });
    }
  };

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Access denied. Admin or Principal only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#12325b] via-[#1c4a80] to-[#12325b] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/manage-gallery-images")}
            className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Gallery Steps
          </button>
        </div>

        <div className="mb-8 rounded-2xl bg-white p-6 shadow-[0_16px_40px_rgba(18,50,91,0.25)]">
          <h1 className="text-3xl font-bold text-[#12325b] mb-2">
            {sectionTitle}
          </h1>
          <p className="text-gray-600">{sectionDescription}</p>
        </div>

        {saveState.success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {saveState.success}
          </div>
        )}
        {saveState.error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {saveState.error}
          </div>
        )}

        <div className="bg-white rounded-2xl p-8 mb-8 shadow-[0_16px_40px_rgba(18,50,91,0.25)]">
          <h2 className="text-2xl font-bold text-[#12325b] mb-6">
            Upload New Media
          </h2>

          <div className="flex gap-4 items-center">
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-blue-200 rounded-lg p-8 hover:border-blue-500 transition text-center">
                <Upload className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <p className="text-gray-700 font-medium mb-1">
                  Click to select image or video
                </p>
                <p className="text-gray-500 text-sm">
                  Support: JPG, PNG, MP4, WebM, etc.
                </p>
              </div>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                disabled={uploading}
                className="hidden"
              />
            </label>

            {uploading && (
              <div className="flex items-center gap-2 text-blue-600">
                <Loader className="w-6 h-6 animate-spin" />
                <span>Uploading...</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-[0_16px_40px_rgba(18,50,91,0.25)]">
          <h2 className="text-2xl font-bold text-[#12325b] mb-2">
            All Media ({images.length})
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            Manage all uploaded images and videos for this section.
          </p>

          {images.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No media yet. Upload above to get started.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {images.map((image, index) => {
                const declaredType = String(image?.type || "").toLowerCase();
                const isVideo =
                  declaredType === "video" || isVideoUrl(String(image?.url || ""));
                return (
                  <div
                    key={index}
                    className="relative group rounded-lg overflow-hidden bg-slate-100 aspect-square"
                  >
                    {isVideo ? (
                      <video
                        src={image.url}
                        className="w-full h-full object-cover"
                        controls
                      />
                    ) : (
                      <img
                        src={image.url}
                        alt="Gallery"
                        className="w-full h-full object-cover"
                      />
                    )}

                    <button
                      onClick={() => handleEditImage(image)}
                      className="absolute top-2 left-2 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition"
                      title="Edit image"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>

                    <button
                      onClick={() => handleDeleteImage(image.url, image)}
                      disabled={saveState.saving}
                      className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition"
                      title="Delete image"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showEditModal && editingImage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full border border-slate-200">
            <h2 className="text-2xl font-bold text-[#12325b] mb-6">
              Edit Media
            </h2>

            <div className="space-y-4">
              <div>
                <p className="text-gray-700 text-sm font-semibold mb-2">
                  Current Media
                </p>
                {editingImage.url?.includes("video") ||
                String(editingImage?.type || "").toLowerCase() === "video" ||
                isVideoUrl(String(editingImage?.url || "")) ? (
                  <video
                    src={editingImage.url}
                    className="w-full h-40 object-cover rounded"
                    controls
                  />
                ) : (
                  <img
                    src={editingImage.url}
                    alt="Current"
                    className="w-full h-40 object-cover rounded"
                  />
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-2">
                  Replace Media (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => setEditFile(e.target.files?.[0])}
                  className="block w-full text-gray-700 text-sm border border-slate-300 rounded p-2"
                />
                {editFile && (
                  <p className="text-green-600 text-sm mt-2">
                    ✓ New file: {editFile.name}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingImage(null);
                    setEditFile(null);
                  }}
                  className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saveState.saving}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-semibold"
                >
                  {saveState.saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
